/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import { timeout } from 'lumen/utils/waiters';

export interface HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature {
  Args: {
    params?: {
      isLoading: boolean;
      searchValues?: {
        isSearchActive: boolean;
        searchValue: string;
      };
      segmentationName: string;
      updateIsSearchActive: (value: boolean) => void;
      updateSearchValue: (value: string) => void;
    };
  };
}

export default class HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBase<
  T extends
    HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature =
    HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature,
> extends Component<T> {
  @service declare intl: Services['intl'];

  @service declare riSelections: Services['ri-selections'];

  @tracked tempSearchValue = '';

  /**
   * This updates the search value in the controller
   * We keep using the temp search value at all times and assume nothing went wrong updating the value
   * If something did go wrong, we'll send the correct value down to the controller next time
   */
  updateSearchValueTask = task(
    { restartable: true },
    async (event): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      set(this, 'tempSearchValue', event.target.value);
      await timeout();
      this.args.params?.updateSearchValue(this.tempSearchValue);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get breakdownDetailRoute() {
    return `${this.riSelections.currentRoute}.breakdown-detail`;
  }

  @computed('args.params.isLoading')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return this.args.params?.isLoading;
  }

  @computed('searchValues.isSearchActive')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSearchActive() {
    return this.searchValues?.isSearchActive;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchPlaceholder() {
    const segmentationName =
      this.riSelections.isCancellationsOnlyView &&
      this.segmentationName === RI_BREAKDOWNS.PICKUP
        ? 'cancellation_pickup'
        : this.segmentationName;
    return this.intl.t(`revenue_insight.search.${segmentationName}`);
  }

  @computed('searchValues.searchValue')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchValue() {
    return this.searchValues?.searchValue;
  }

  @computed('args.params.searchValues')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchValues() {
    return this.args.params?.searchValues;
  }

  @computed('args.params.segmentationName')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationName() {
    return this.args.params?.segmentationName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get vsText() {
    if (this.segmentationName === 'pickup') {
      return this.riSelections.vsReferencePeriodText;
    }

    return this.riSelections.vsText;
  }

  @action disableSearch(): void {
    this.toggleSearch(false);
  }

  @action toggleSearch(isEnabled: boolean): void {
    this.args.params?.updateIsSearchActive(isEnabled);
  }
}
